import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { highlightParts, fetchAllCompaniesBasic } from '../lib/employeeSearch';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Check, X, Clock, AlertTriangle, Calendar, FileText, Loader2, Mail, Search, GraduationCap, MinusCircle, TimerOff, Paperclip, Archive, ArchiveRestore, Ban } from 'lucide-react';
import { TablePagination } from '../components/TablePagination';

// Ortak yardımcı fonksiyonlar ve sabitler
import { formatDate, maskTcNo, validateTcKimlikNo, isDocumentExpired, getActualDocStatus, calculateOverallStatus } from '../lib/helpers';
import { EMPLOYEE_DOCS } from '../components/employees/constants';
import { DocumentViewerDialog } from '../components/employees/DocumentViewerDialog';
import { confirmDialog } from '../components/ConfirmHost';
import { NationalityToggle, PassportTag, cleanPassport } from '../components/employees/Nationality';
import { BlockEmployeeDialog } from '../components/employees/BlockEmployeeDialog';
import { useScreenReady, markScreenStart } from '../lib/perfScreen';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeesPage = () => {
  const { user, hasLocationAccess, canCreate, canEdit, canDelete, isReviewer } = useAuth();
  const [blockTarget, setBlockTarget] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]); // Lokasyonlar
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('all'); // Lokasyon filtresi
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedActiveFilter, setSelectedActiveFilter] = useState('active'); // Aktif/Pasif filtresi
  const [dateWarning, setDateWarning] = useState(null);
  const [systemSettings, setSystemSettings] = useState({ tc_required: false });
  const [tcError, setTcError] = useState(null);
  const [formData, setFormData] = useState({ 
    company_id: '', 
    full_name: '',
    position: '',
    tc_kimlik_no: '',
    is_active: true,
    documents: {}
  });

  // For document status popover
  const [openPopover, setOpenPopover] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [showDeficiencyList, setShowDeficiencyList] = useState(false);
  const [selectedDocKey, setSelectedDocKey] = useState(null);
  const [selectedEmployeeForDoc, setSelectedEmployeeForDoc] = useState(null);
  const [tempDate, setTempDate] = useState('');
  const [selectedDeficiencies, setSelectedDeficiencies] = useState([]); // Çoklu eksiklik seçimi
  
  // For PDF report
  const [selectedForReport, setSelectedForReport] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // For email modal
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [toEmails, setToEmails] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Personel arama
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [debouncedQuery, setDebouncedQuery] = useState(employeeSearchQuery);
  const [loadedQuery, setLoadedQuery] = useState(null);
  useScreenReady(debouncedQuery.length >= 3 ? 'personel_arama' : null, loadedQuery === debouncedQuery, debouncedQuery);
  const fetchSeq = useRef(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // MYK filtresi
  const [selectedMykFilter, setSelectedMykFilter] = useState('all');
  const [mykStatusFilter, setMykStatusFilter] = useState('all');
  
  // MYK list state
  const [mykList, setMykList] = useState([]);
  const [selectedMykObj, setSelectedMykObj] = useState(null); // Seçilen MYK objesi {name, code, description}
  const [showMykSelector, setShowMykSelector] = useState(false);
  const [mykSelectorForEmployee, setMykSelectorForEmployee] = useState(null);
  const [mykSelectorStatus, setMykSelectorStatus] = useState(null); // 'onay' or 'eksik'
  const [mykSearchQuery, setMykSearchQuery] = useState('');
  const [mykValidityDate, setMykValidityDate] = useState('');
  const [showDiplomaInput, setShowDiplomaInput] = useState(false);
  const [diplomaText, setDiplomaText] = useState('');

  // Highlight matching text
  const highlightMatch = (text, query) => highlightParts(text, query).map((part, i) =>
    typeof part === 'string' ? part : <span key={i} className="text-red-600 font-bold">{part.text}</span>
  );

  useEffect(() => {
    fetchData();
    fetchSystemSettings();
  }, []);

  // Arama sunucuya 300 ms gecikmeyle gider; değişince 1. sayfaya dönülür
  useEffect(() => {
    const t = setTimeout(() => {
      const next = employeeSearchQuery.length >= 3 ? employeeSearchQuery : '';
      if (next !== debouncedQuery) { setDebouncedQuery(next); setCurrentPage(1); }
    }, 300);
    return () => clearTimeout(t);
  }, [employeeSearchQuery, debouncedQuery]);

  // İlk yükleme fetchData içinde yapılır; burada yalnız filtre/sayfa değişimleri (çift istek olmasın)
  const skipFirstFetch = useRef(true);
  useEffect(() => {
    if (skipFirstFetch.current) { skipFirstFetch.current = false; return; }
    fetchEmployees();
  }, [selectedCompanyFilter, currentPage, pageSize, showArchived, debouncedQuery, selectedLocationFilter, selectedActiveFilter]);

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/system-settings`);
      setSystemSettings(response.data || { tc_required: false });
    } catch (error) {
      console.error('Sistem ayarlari yuklenemedi');
    }
  };

  const fetchData = async () => {
    try {
      const [companiesRes, templatesRes, mykRes, locationsRes] = await Promise.all([
        fetchAllCompaniesBasic(API),
        axios.get(`${API}/deficiency-templates`),
        axios.get(`${API}/myk-list`),
        axios.get(`${API}/locations`)
      ]);
      setCompanies(companiesRes);
      setTemplates(templatesRes.data);
      setMykList(mykRes.data);
      setLocations(locationsRes.data);
      await fetchEmployees();
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    const seq = ++fetchSeq.current;
    try {
      let params = `page=${currentPage}&page_size=${pageSize}&active=${selectedActiveFilter}&masked=true`;
      if (selectedCompanyFilter !== 'all') params += `&company_id=${selectedCompanyFilter}`;
      if (selectedLocationFilter !== 'all') params += `&location_id=${encodeURIComponent(selectedLocationFilter)}`;
      if (debouncedQuery.length >= 3) params += `&q=${encodeURIComponent(debouncedQuery)}`;
      if (showArchived) params += '&archived=true';
      const response = await axios.get(`${API}/employees?${params}`);
      if (seq !== fetchSeq.current) return; // eski istek yeni sonucun üzerine yazmasın
      const data = response.data;
      setEmployees(data.items || data);
      setLoadedQuery(debouncedQuery);
      setTotalItems(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      if (seq !== fetchSeq.current) return;
      toast.error('Personel yüklenemedi');
    }
  };

  // Sayfa değiştiğinde
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sayfa boyutu değiştiğinde
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // İlk sayfaya dön
  };

  // Firma'nın lokasyonunu bul
  const getCompanyLocationId = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.location_id;
  };

  // Firma listesi backend yetki kapsamıyla gelir (lokasyonu tanımsız yetkili firmalar dahil)
  const authorizedCompanies = companies;
  
  // Kullanıcının yetkili olduğu lokasyonları filtrele
  const authorizedLocations = locations.filter(loc => hasLocationAccess(loc.id));

  const getTemplatesForDoc = (docKey) => {
    return templates.filter(t => t.document_type === docKey);
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || '-';
  };

  const getLocationName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (!company) return '-';
    const location = locations.find(l => l.id === company.location_id);
    return location?.name || '-';
  };

  const getOverallStatusBadge = (employee) => {
    const docKeys = EMPLOYEE_DOCS.map(d => d.key);
    const overall = calculateOverallStatus(employee.documents, docKeys);
    const Icon = overall.status === 'onay' ? Check : overall.status === 'eksik' ? X : Clock;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className={`${overall.color} cursor-default`} data-testid={`overall-status-${employee.id}`}>
            <Icon className="h-3.5 w-3.5" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-slate-800 text-white">
          <p>{overall.label}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  // MYK durumu: onay (geçerli) / suresi_gecmis (onaylı ama tarihi geçmiş) / beklemede / eksik
  const mykToday = new Date().toLocaleDateString('sv-SE');
  const mykStatusOf = (emp) => {
    const d = emp.documents || {};
    const st = d.mesleki_yeterlilik;
    if (st !== 'onay') return st;
    const v = String(d.mesleki_yeterlilik_date || d.mesleki_yeterlilik_validity || '').slice(0, 10);
    return v && v < mykToday ? 'suresi_gecmis' : 'onay';
  };

  // Personel listesini genel duruma göre filtrele
  const filteredEmployees = employees.filter(employee => {
    // Yetki kapsamı backend'de uygulanır; seçili lokasyon filtresi aşağıda
    const companyLocationId = getCompanyLocationId(employee.company_id);
    
    // Lokasyon filtresi (AVM bazında)
    if (selectedLocationFilter !== 'all' && companyLocationId !== selectedLocationFilter) return false;
    
    // Firma filtresi
    if (selectedCompanyFilter !== 'all' && employee.company_id !== selectedCompanyFilter) return false;
    
    // Aktif/Pasif filtresi
    if (selectedActiveFilter === 'active' && employee.is_active === false) return false;
    if (selectedActiveFilter === 'passive' && employee.is_active !== false) return false;
    
    // Durum filtresi
    if (selectedStatusFilter !== 'all') {
      const docKeys = EMPLOYEE_DOCS.map(d => d.key);
      const overall = calculateOverallStatus(employee.documents, docKeys);
      if (overall.status !== selectedStatusFilter) return false;
    }
    
    // Ad Soyad / T.C. araması sunucuda (tüm kayıtlarda) yapılır
    
    // MYK filtresi
    if (selectedMykFilter !== 'all') {
      const employeeMyk = employee.documents?.mesleki_yeterlilik_myk || '';
      if (employeeMyk !== selectedMykFilter) return false;
    }
    if (mykStatusFilter !== 'all' && mykStatusOf(employee) !== mykStatusFilter) return false;
    
    return true;
  });

  // Personellerde kayıtlı benzersiz MYK listesi (sadece onaylı ve süresi geçmemiş kişiler sayılır)
  const getUniqueMykFromEmployees = () => {
    const mykSet = new Map();
    employees.forEach(emp => {
      const mykName = emp.documents?.mesleki_yeterlilik_myk;
      if (mykName && mykName.trim() && mykStatusOf(emp) === 'onay') {
        // MYK sayısını hesapla
        const count = mykSet.get(mykName) || 0;
        mykSet.set(mykName, count + 1);
      }
    });
    // Map'i array'e çevir ve sırala
    return Array.from(mykSet.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  };

  const uniqueMykList = getUniqueMykFromEmployees();

  // Lokasyon değiştiğinde firma filtresini sıfırla
  const handleLocationFilterChange = (locationId) => {
    setSelectedLocationFilter(locationId);
    setSelectedCompanyFilter('all'); // Firma filtresini sıfırla
    setCurrentPage(1);
  };

  // Seçilen lokasyondaki firmaları getir
  const getCompaniesForSelectedLocation = () => {
    if (selectedLocationFilter === 'all') {
      return companies;
    }
    return companies.filter(c => c.location_id === selectedLocationFilter);
  };

  // Eksik veya beklemede olan personelleri kontrol et (sadece aktifler için)
  const isEmployeeDeficient = (employee) => {
    if (employee.is_active === false) return false; // Pasifler için checkbox yok
    const docKeys = EMPLOYEE_DOCS.map(d => d.key);
    const overall = calculateOverallStatus(employee.documents, docKeys);
    return overall.status === 'eksik' || overall.status === 'beklemede';
  };

  // Checkbox toggle
  const toggleReportSelection = (employeeId) => {
    setSelectedForReport(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Tümünü seç/kaldır
  const toggleSelectAll = () => {
    const deficientEmployees = filteredEmployees.filter(isEmployeeDeficient);
    const allSelected = deficientEmployees.every(e => selectedForReport.includes(e.id));
    
    if (allSelected) {
      setSelectedForReport(prev => prev.filter(id => !deficientEmployees.map(e => e.id).includes(id)));
    } else {
      const newSelection = [...new Set([...selectedForReport, ...deficientEmployees.map(e => e.id)])];
      setSelectedForReport(newSelection);
    }
  };

  // PDF rapor oluştur
  const generatePdfReport = async () => {
    if (selectedForReport.length === 0) {
      toast.error('Lütfen rapor için personel seçin');
      return;
    }
    
    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/reports/employee-deficiency-pdf`, {
        employee_ids: selectedForReport
      }, { responseType: 'blob' });
      
      // Download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `personel_eksik_rapor_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF rapor oluşturuldu');
      setSelectedForReport([]);
    } catch (error) {
      toast.error('Rapor oluşturulamadı');
    } finally {
      setGeneratingReport(false);
    }
  };

  // E-posta modal'ını aç
  const openEmailModal = async () => {
    if (selectedForReport.length === 0) {
      toast.error('Lütfen e-posta için personel seçin');
      return;
    }
    
    // Daha önce kullanılan e-posta adreslerini getir
    try {
      const response = await axios.get(`${API}/email-recipients`);
      setEmailRecipients(response.data || []);
    } catch (error) {
      console.error('Could not fetch recipients');
    }
    
    setEmailModalOpen(true);
  };

  // E-posta gönder
  const sendEmailReport = async () => {
    const toList = toEmails.split(',').map(e => e.trim()).filter(e => e);
    const ccList = ccEmails.split(',').map(e => e.trim()).filter(e => e);
    
    if (toList.length === 0) {
      toast.error('En az bir alıcı e-posta adresi girin');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/send-deficiency-email`, {
        report_type: 'employee',
        ids: selectedForReport,
        to_emails: toList,
        cc_emails: ccList
      });
      
      toast.success('E-posta başarıyla gönderildi');
      setEmailModalOpen(false);
      setToEmails('');
      setCcEmails('');
      setSelectedForReport([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-posta gönderilemedi');
    } finally {
      setSendingEmail(false);
    }
  };

  // Hızlı e-posta ekle
  const addQuickEmail = (email, field) => {
    if (field === 'to') {
      setToEmails(prev => prev ? `${prev}, ${email}` : email);
    } else {
      setCcEmails(prev => prev ? `${prev}, ${email}` : email);
    }
  };

  // MYK seçim için - Mesleki Yeterlilik özel işlem
  const openMykSelector = (employee, status) => {
    setMykSelectorForEmployee(employee);
    setMykSelectorStatus(status);
    setSelectedMykObj(null);
    setMykSearchQuery('');
    setMykValidityDate('');
    setShowDiplomaInput(false);
    setDiplomaText('');
    setShowMykSelector(true);
  };

  // Listeden MYK seçimi - geçerlilik tarihli
  const handleMykListSelection = async (mykObj, validityDate) => {
    if (!mykSelectorForEmployee || !mykObj) return;
    if (!validityDate && mykSelectorStatus === 'onay') {
      toast.error('Geçerlilik tarihi giriniz');
      return;
    }
    
    const employee = mykSelectorForEmployee;
    const status = mykSelectorStatus;
    
    // Kod ve belge adını birleştir (örn: "12UY0105-3 Elektrik Tesisatçısı Seviye 3")
    const mykDisplayName = mykObj.code ? `${mykObj.code} ${mykObj.name}` : mykObj.name;
    
    try {
      const updatedDocs = { ...employee.documents };
      updatedDocs.mesleki_yeterlilik = status;
      updatedDocs.mesleki_yeterlilik_myk = mykDisplayName;
      updatedDocs.mesleki_yeterlilik_type = 'list';
      
      if (status === 'eksik') {
        delete updatedDocs.mesleki_yeterlilik_date;
        updatedDocs.mesleki_yeterlilik_note = `Seçili belge: ${mykDisplayName}`;
      } else if (status === 'onay') {
        updatedDocs.mesleki_yeterlilik_date = validityDate;
        delete updatedDocs.mesleki_yeterlilik_note;
      }
      
      await axios.put(`${API}/employees/${employee.id}`, {
        company_id: employee.company_id,
        name: employee.name,
        surname: employee.surname,
        position: employee.position,
        documents: updatedDocs
      });
      
      toast.success(`MYK belgesi ${status === 'onay' ? 'onaylandı' : 'eksik olarak işaretlendi'}`);
      closeMykSelector();
      fetchEmployees();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  // Diploma seçimi - süresiz
  const handleDiplomaSelection = async () => {
    if (!mykSelectorForEmployee || !diplomaText.trim()) {
      toast.error('Diploma adını giriniz');
      return;
    }
    
    const employee = mykSelectorForEmployee;
    const status = mykSelectorStatus;
    
    try {
      const updatedDocs = { ...employee.documents };
      updatedDocs.mesleki_yeterlilik = status;
      updatedDocs.mesleki_yeterlilik_myk = diplomaText.trim();
      updatedDocs.mesleki_yeterlilik_type = 'diploma';
      
      if (status === 'eksik') {
        delete updatedDocs.mesleki_yeterlilik_date;
        updatedDocs.mesleki_yeterlilik_note = `Diploma: ${diplomaText.trim()}`;
      } else if (status === 'onay') {
        delete updatedDocs.mesleki_yeterlilik_date; // Süresiz
        delete updatedDocs.mesleki_yeterlilik_note;
      }
      
      await axios.put(`${API}/employees/${employee.id}`, {
        company_id: employee.company_id,
        name: employee.name,
        surname: employee.surname,
        position: employee.position,
        documents: updatedDocs
      });
      
      toast.success(`Diploma ${status === 'onay' ? 'onaylandı' : 'eksik olarak işaretlendi'}`);
      closeMykSelector();
      fetchEmployees();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const closeMykSelector = () => {
    setShowMykSelector(false);
    setMykSelectorForEmployee(null);
    setSelectedMykObj(null);
    setMykSearchQuery('');
    setMykValidityDate('');
    setShowDiplomaInput(false);
    setDiplomaText('');
    setOpenPopover(null);
  };

  // MYK listesini arama sorgusuna göre filtrele (tüm sütunlarda ara)
  const filteredMykList = mykList.filter(myk => {
    const query = mykSearchQuery.toLowerCase();
    return (
      myk.name?.toLowerCase().includes(query) ||
      myk.code?.toLowerCase().includes(query) ||
      myk.description?.toLowerCase().includes(query)
    );
  });

  const handleOpenDialog = async (listEmployee = null) => {
    setTcError(null);
    let employee = listEmployee;
    if (employee) {
      // Listede T.C. yıldızlı; formda tam numara için personel ayrıca çekilir
      try { employee = { ...listEmployee, ...(await axios.get(`${API}/employees/${employee.id}`)).data }; } catch { toast.error('Personel bilgisi alınamadı'); return; }
      setEditingEmployee(employee);
      setFormData({ 
        company_id: employee.company_id,
        full_name: `${employee.name} ${employee.surname}`.trim(),
        position: employee.position || '',
        tc_kimlik_no: employee.tc_kimlik_no || '',
        nationality: employee.nationality || 'tc',
        is_active: employee.is_active !== false, // undefined veya true ise aktif
        documents: employee.documents || {}
      });
    } else {
      setEditingEmployee(null);
      setFormData({ 
        company_id: '', 
        full_name: '',
        position: '',
        tc_kimlik_no: '',
        nationality: 'tc',
        is_active: true,
        documents: {}
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const foreign = formData.nationality === 'yabanci';
    if (foreign && !formData.tc_kimlik_no) {
      setTcError('Pasaport No zorunludur');
      toast.error('Yabancı uyruklu personelde Pasaport No zorunludur');
      return;
    }
    // T.C. doğrulama (yabancıda pasaport no için 11 hane/algoritma kontrolü yapılmaz)
    if (foreign) {
      // pasaport: sadece boş olmasın
    } else if (formData.tc_kimlik_no) {
      const tcValidation = validateTcKimlikNo(formData.tc_kimlik_no);
      if (!tcValidation.isValid) {
        setTcError(tcValidation.error);
        toast.error(tcValidation.error);
        return;
      }
    } else if (systemSettings.tc_required && !editingEmployee) {
      // Yeni kayıtta T.C. zorunlu
      setTcError('T.C. Kimlik No zorunludur');
      toast.error('T.C. Kimlik No zorunludur');
      return;
    }
    
    setTcError(null);
    
    try {
      const nameParts = formData.full_name.trim().split(' ');
      const surname = nameParts.length > 1 ? nameParts.pop() : '';
      const name = nameParts.join(' ') || formData.full_name.trim();
      
      const submitData = {
        company_id: formData.company_id,
        name: name,
        surname: surname,
        position: formData.position,
        tc_kimlik_no: formData.tc_kimlik_no || null,
        nationality: formData.nationality || 'tc',
        is_active: formData.is_active,
        documents: formData.documents
      };

      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, submitData);
        toast.success('Personel güncellendi');
        const changed = ['name', 'surname', 'position', 'tc_kimlik_no'].some(k => (submitData[k] || '') !== (editingEmployee[k] || ''));
        if (changed) {
          axios.get(`${API}/employees/${editingEmployee.id}/sealed-permits`).then(r => r.data.count && toast.info('Bu değişiklik mevcut taleplere yansımaz', {
            description: 'Yansıması için personeli talepten çıkarıp tekrar ekleyin.', duration: 9000,
          })).catch((e) => console.warn('Mühürlü talep kontrolü yapılamadı', e?.response?.status || e?.message));
        }
        setDialogOpen(false);
      } else {
        await axios.post(`${API}/employees`, submitData);
        toast.success('Personel kaydedildi');
        setFormData({ 
          company_id: formData.company_id, 
          full_name: '',
          position: '',
          tc_kimlik_no: '',
          is_active: true,
          documents: {}
        });
      }
      fetchEmployees();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Personel kaydedilemedi, lütfen bilgileri kontrol edin';
      toast.error(errorMsg);
      if (errorMsg.includes('T.C.')) {
        setTcError(errorMsg);
      }
    }
  };

  // Silme = arşive alma: kayıt ve belgeler korunur, tamamlanan taleplerde personel görünmeye devam eder
  const handleUnblock = async (employee) => {
    if (!(await confirmDialog({ title: 'Blok kaldırılsın mı?', description: `${employee.name} ${employee.surname} için blok kaldırılacak.`, confirmLabel: 'Bloğu Kaldır' }))) return;
    try {
      await axios.delete(`${API}/employees/${employee.id}/block`);
      toast.success('Blok kaldırıldı');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Blok kaldırılamadı');
    }
  };

  const handleDelete = async (id) => {
    const active = (await axios.get(`${API}/employees/${id}/active-permits`).catch((e) => { console.warn('Aktif talep kontrolü yapılamadı', e?.response?.status || e?.message); return { data: [] }; })).data;
    if (active.length && !(await confirmDialog({
      title: 'Personelin onaylı talebi var!',
      description: `Bu personel şu onaylı (aktif) taleplerde yer alıyor: ${active.map(p => `#${p.request_number}`).join(', ')}. Arşive alırsanız bu taleplerde adı üstü çizili "Silinmiştir" olarak görünecek. Yine de arşive almak istiyor musunuz?`,
      confirmLabel: 'Evet, Arşive Al',
    }))) return;
    if (!active.length && !(await confirmDialog({ title: 'Personel arşive alınsın mı?', description: 'Personel listeden kaldırılıp arşive alınır. Belgeleri ve yer aldığı talepler korunur; istediğinizde Arşiv sekmesinden geri getirebilirsiniz.', confirmLabel: 'Arşive Al' }))) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success('Personel arşive alındı');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Arşive alma başarısız');
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/employees/${id}/restore`);
      toast.success('Personel arşivden geri getirildi');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Geri getirme başarısız');
    }
  };

  // Validate dates - ISG and Saglik dates must be same or after SGK date
  const validateDates = (employee, docKey, newDate) => {
    const docs = employee.documents || {};
    const sgkDate = docs.sgk_ise_giris_date;
    
    // Only validate for isg_egitimi and saglik_raporu
    if ((docKey === 'isg_egitimi' || docKey === 'saglik_raporu') && sgkDate && newDate) {
      if (new Date(newDate) < new Date(sgkDate)) {
        const docLabel = docKey === 'isg_egitimi' ? 'İSG Eğitimi' : 'Sağlık Raporu';
        return {
          isValid: false,
          message: `${docLabel} tarihi (${newDate}), SGK işe giriş tarihinden (${sgkDate}) önce olamaz! Belge durumu "Beklemede" olarak ayarlandı.`
        };
      }
    }
    return { isValid: true, message: null };
  };

  // Handle document status change
  const handleDocStatusChange = async (employee, docKey, newStatus, docConfig) => {
    // SGK işe giriş tarihi sadece İSG Eğitimi ve Sağlık Raporu için gerekir (kapsam dışı hariç)
    if (['isg_egitimi', 'saglik_raporu'].includes(docKey) && newStatus !== 'kapsam_disi') {
      const sgkDate = employee.documents?.sgk_ise_giris_date;
      if (!sgkDate) {
        setDateWarning('Önce SGK İşe Giriş tarihini girmelisiniz!');
        setOpenPopover(null);
        return;
      }
    }

    if (newStatus === 'eksik') {
      // Bildirge eksik yapılırsa, Eğitim ve Sağlık otomatik beklemede olsun
      if (docKey === 'sgk_ise_giris') {
        try {
          const updatedDocs = {
            ...employee.documents,
            sgk_ise_giris: 'eksik',
            sgk_ise_giris_note: '',
            // Eğitim ve Sağlık'ı beklemede yap
            isg_egitimi: 'beklemede',
            isg_egitimi_note: '',
            isg_egitimi_date: null,
            saglik_raporu: 'beklemede',
            saglik_raporu_note: '',
            saglik_raporu_date: null
          };
          
          await axios.put(`${API}/employees/${employee.id}`, {
            company_id: employee.company_id,
            name: employee.name,
            surname: employee.surname,
            position: employee.position,
            documents: updatedDocs
          });
          
          setOpenPopover(null);
          fetchEmployees();
        } catch (error) {
          console.error('SGK işe giriş durumu güncellenemedi', error?.response?.status || error?.message);
          toast.error('Güncelleme başarısız');
        }
        return;
      }
      
      setSelectedEmployeeForDoc(employee);
      setSelectedDocKey(docKey);
      setShowDeficiencyList(true);
      setOpenPopover(null);
    } else if (newStatus === 'onay') {
      // Check if date is required - use tempDate first, then existing date
      const dateToSave = tempDate || employee.documents?.[`${docKey}_date`];
      if (docConfig.hasDate && !dateToSave) {
        setDateWarning('Onay vermek için önce tarih girmelisiniz!');
        return;
      }
      
      // Special handling for SGK - check and update related docs
      if (docKey === 'sgk_ise_giris') {
        const { updatedDocs, needsUpdate, warnings } = await checkAndUpdateRelatedDocs(employee, dateToSave);
        
        try {
          updatedDocs.sgk_ise_giris = 'onay';
          updatedDocs.sgk_ise_giris_note = '';
          updatedDocs.sgk_ise_giris_date = dateToSave;
          
          await axios.put(`${API}/employees/${employee.id}`, {
            company_id: employee.company_id,
            name: employee.name,
            surname: employee.surname,
            position: employee.position,
            documents: updatedDocs
          });
          
          if (warnings.length > 0) {
            setDateWarning(warnings.join('\n'));
          } else {
            toast.success('Belge onaylandı');
          }
          setOpenPopover(null);
          setTempDate('');
          fetchEmployees();
        } catch (error) {
          toast.error('Güncelleme başarısız');
        }
        return;
      }
      
      // Validate date for ISG and Saglik against SGK date
      if (docKey === 'isg_egitimi' || docKey === 'saglik_raporu') {
        const validation = validateDates(employee, docKey, dateToSave);
        if (!validation.isValid) {
          // Set to beklemede and show warning
          try {
            const updatedDocs = {
              ...employee.documents,
              [docKey]: 'beklemede',
              [`${docKey}_note`]: '',
              [`${docKey}_date`]: dateToSave
            };
            
            await axios.put(`${API}/employees/${employee.id}`, {
              company_id: employee.company_id,
              name: employee.name,
              surname: employee.surname,
              position: employee.position,
              documents: updatedDocs
            });
            
            setDateWarning(validation.message);
            setOpenPopover(null);
            setTempDate('');
            fetchEmployees();
          } catch (error) {
            toast.error('Güncelleme başarısız');
          }
          return;
        }
      }
      
      try {
        const updatedDocs = {
          ...employee.documents,
          [docKey]: newStatus,
          [`${docKey}_note`]: '',
          [`${docKey}_date`]: dateToSave
        };
        
        await axios.put(`${API}/employees/${employee.id}`, {
          company_id: employee.company_id,
          name: employee.name,
          surname: employee.surname,
          position: employee.position,
          documents: updatedDocs
        });
        
        toast.success('Belge onaylandı');
        setOpenPopover(null);
        setTempDate('');
        fetchEmployees();
      } catch (error) {
        toast.error('Güncelleme başarısız');
      }
    } else {
      // Beklemede - no date required
      try {
        const updatedDocs = {
          ...employee.documents,
          [docKey]: newStatus,
          [`${docKey}_note`]: ''
        };
        
        await axios.put(`${API}/employees/${employee.id}`, {
          company_id: employee.company_id,
          name: employee.name,
          surname: employee.surname,
          position: employee.position,
          documents: updatedDocs
        });
        
        toast.success('Belge durumu güncellendi');
        setOpenPopover(null);
        fetchEmployees();
      } catch (error) {
        toast.error('Güncelleme başarısız');
      }
    }
  };

  // Handle date change
  const handleDateChange = async (employee, docKey, newDate) => {
    // Validate date
    const warning = validateDates(employee, docKey, newDate);
    if (warning) {
      setDateWarning(warning);
      return;
    }

    try {
      const updatedDocs = {
        ...employee.documents,
        [`${docKey}_date`]: newDate
      };
      
      await axios.put(`${API}/employees/${employee.id}`, {
        company_id: employee.company_id,
        name: employee.name,
        surname: employee.surname,
        documents: updatedDocs
      });
      
      toast.success('Tarih güncellendi');
      setOpenPopover(null);
      setTempDate('');
      fetchEmployees();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  // Toggle deficiency checkbox
  const toggleDeficiency = (templateText) => {
    setSelectedDeficiencies(prev => 
      prev.includes(templateText) 
        ? prev.filter(t => t !== templateText)
        : [...prev, templateText]
    );
  };

  // Handle deficiency save (multiple)
  const handleDeficiencySave = async () => {
    if (!selectedEmployeeForDoc || !selectedDocKey || selectedDeficiencies.length === 0) return;
    
    try {
      const deficiencyText = selectedDeficiencies.join(', ');
      const updatedDocs = {
        ...selectedEmployeeForDoc.documents,
        [selectedDocKey]: 'eksik',
        [`${selectedDocKey}_note`]: deficiencyText
      };
      
      // Eksik durumunda tarih ve geçerlilik bilgilerini sil
      delete updatedDocs[`${selectedDocKey}_date`];
      delete updatedDocs[`${selectedDocKey}_validity`];
      
      await axios.put(`${API}/employees/${selectedEmployeeForDoc.id}`, {
        company_id: selectedEmployeeForDoc.company_id,
        name: selectedEmployeeForDoc.name,
        surname: selectedEmployeeForDoc.surname,
        position: selectedEmployeeForDoc.position,
        documents: updatedDocs
      });
      
      setShowDeficiencyList(false);
      setSelectedEmployeeForDoc(null);
      setSelectedDocKey(null);
      setSelectedDeficiencies([]);
      fetchEmployees();
    } catch (error) {
      console.error('Güncelleme başarısız');
    }
  };

  // Check and update ISG/Saglik statuses when SGK date changes
  const checkAndUpdateRelatedDocs = async (employee, newSgkDate) => {
    const docs = employee.documents || {};
    let needsUpdate = false;
    let warnings = [];
    const updatedDocs = { ...docs, sgk_ise_giris_date: newSgkDate };
    
    // Check ISG Egitimi
    const isgDate = docs.isg_egitimi_date;
    if (isgDate && docs.isg_egitimi === 'onay' && new Date(isgDate) < new Date(newSgkDate)) {
      updatedDocs.isg_egitimi = 'beklemede';
      warnings.push(`İSG Eğitimi tarihi (${isgDate}) SGK giriş tarihinden önce olduğu için "Beklemede" yapıldı.`);
      needsUpdate = true;
    }
    
    // Check Saglik Raporu
    const saglikDate = docs.saglik_raporu_date;
    if (saglikDate && docs.saglik_raporu === 'onay' && new Date(saglikDate) < new Date(newSgkDate)) {
      updatedDocs.saglik_raporu = 'beklemede';
      warnings.push(`Sağlık Raporu tarihi (${saglikDate}) SGK giriş tarihinden önce olduğu için "Beklemede" yapıldı.`);
      needsUpdate = true;
    }
    
    return { updatedDocs, needsUpdate, warnings };
  };

  const getDocStatusBadge = (employee, docKey, docConfig) => {
    const rawStatus = employee.documents?.[docKey];
    const date = employee.documents?.[`${docKey}_date`];
    const validity = employee.documents?.[`${docKey}_validity`];
    const note = employee.documents?.[`${docKey}_note`];
    const mykName = employee.documents?.mesleki_yeterlilik_myk;
    
    // Süresi dolmuş kontrolü - gerçek statüyü al
    const status = getActualDocStatus(employee.documents, docKey);
    
    
    // Mesleki Yeterlilik için MYK seçimi gerekli
    const isMeslekiYeterlilik = docKey === 'mesleki_yeterlilik';
    
    let badgeClass = '';
    let statusLabel = '';
    let Icon = Clock;
    
    
    switch (status) {
      case 'onay':
        badgeClass = 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 cursor-pointer';
        statusLabel = 'Onay';
        Icon = Check;
        break;
      case 'eksik':
        badgeClass = 'bg-rose-100 text-rose-700 hover:bg-rose-200 cursor-pointer';
        statusLabel = 'Eksik';
        Icon = X;
        break;
      case 'suresi_doldu':
        badgeClass = 'bg-orange-100 text-orange-700 hover:bg-orange-200 cursor-pointer';
        statusLabel = 'Süresi Doldu';
        Icon = TimerOff;
        break;
      case 'kapsam_disi':
        badgeClass = 'bg-slate-100 text-slate-500 hover:bg-slate-200 cursor-pointer';
        statusLabel = 'Kapsam Dışı';
        Icon = MinusCircle;
        break;
      default:
        badgeClass = 'bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer';
        statusLabel = 'Beklemede';
        Icon = Clock;
    }

    // Tooltip içeriği oluştur - sadece onaylı durumda tarih/geçerlilik göster
    const tooltipLines = [statusLabel];
    const mykType = employee.documents?.mesleki_yeterlilik_type;
    
    if (isMeslekiYeterlilik && mykName) {
      // MYK için: sadece kod ve belge adı göster (örn: "12UY0105-3 Elektrik Tesisatçısı Seviye 3")
      // mykName zaten belge adını içeriyor, kodu bulmak için mykList'ten çekebiliriz
      // Ama mykName yeterli - sadece onu gösterelim
      if (mykType === 'diploma') {
        tooltipLines.push(`Diploma: ${mykName}`);
      } else {
        tooltipLines.push(mykName);
      }
      // Geçerlilik bilgisi
      if (status === 'onay') {
        if (mykType === 'diploma') {
          tooltipLines.push('Süresiz');
        } else if (date) {
          tooltipLines.push(`Geçerlilik: ${formatDate(date)}`);
        }
      }
    } else {
      // Diğer belgeler için standart gösterim
      if (status === 'onay' && date) {
        tooltipLines.push(`Tarih: ${formatDate(date)}`);
      }
      if (status === 'onay' && validity) tooltipLines.push(`Geçerlilik: ${formatDate(validity)}`);
      if (status === 'eksik' && note) tooltipLines.push(`Not: ${note}`);
    }

    const hasFile = !!(employee.approved_document_files?.[docKey] || employee.document_files?.[docKey]);
    // Personel sayfasında sadece görüntüleme: tıklayınca belge ve durum gösterilir
    return (
      <div className="flex flex-col items-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              className={`${badgeClass} transition-all relative`}
              onClick={() => setViewerDoc({ employee, docKey, label: docConfig.label })}
              data-testid={`doc-badge-${employee.id}-${docKey}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {hasFile && <Paperclip className="h-2.5 w-2.5 absolute -top-1 -right-1 text-blue-600" data-testid={`doc-file-indicator-${employee.id}-${docKey}`} />}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-slate-800 text-white max-w-xs">
            {tooltipLines.map((line, i) => (
              <p key={i} className={i > 0 ? 'text-slate-300 text-xs mt-1' : ''}>{line}</p>
            ))}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div data-testid="employees-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Personel</h1>
          <p className="text-slate-500 mt-1">Personel kayıtlarını ve belgelerini yönetin</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedForReport.length > 0 && (
            <>
              <Button 
                onClick={generatePdfReport}
                variant="outline"
                className="border-rose-200 text-rose-600 hover:bg-rose-50"
                disabled={generatingReport}
                data-testid="generate-report-btn"
              >
                {generatingReport ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Oluşturuluyor...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    PDF İndir ({selectedForReport.length})
                  </>
                )}
              </Button>
              <Button 
                onClick={openEmailModal}
                variant="outline"
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
                data-testid="email-report-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                E-posta Gönder ({selectedForReport.length})
              </Button>
            </>
          )}
          {canCreate('employees') && (
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="add-employee-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Personel Ekle
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Label>İşletme:</Label>
          <Select value={selectedLocationFilter} onValueChange={handleLocationFilterChange}>
            <SelectTrigger className="w-48" data-testid="location-filter">
              <SelectValue placeholder="İşletme seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm İşletmeler</SelectItem>
              {authorizedLocations.map((location) => (
                <SelectItem key={location.id} value={location.id}>
                  {location.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Label>Firma:</Label>
          <Select value={selectedCompanyFilter} onValueChange={(value) => { setSelectedCompanyFilter(value); setCurrentPage(1); }}>
            <SelectTrigger className="w-48" data-testid="company-filter">
              <SelectValue placeholder="Firma seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Firmalar</SelectItem>
              {getCompaniesForSelectedLocation().map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Label>Durum:</Label>
          <Select value={selectedActiveFilter} onValueChange={(value) => { setSelectedActiveFilter(value); setCurrentPage(1); }}>
            <SelectTrigger className="w-32" data-testid="active-filter">
              <SelectValue placeholder="Durum" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="active">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Aktif
                </span>
              </SelectItem>
              <SelectItem value="passive">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                  Pasif
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Button type="button" variant={showArchived ? 'default' : 'outline'} size="sm" onClick={() => { setShowArchived(v => !v); setCurrentPage(1); }}
          className={showArchived ? 'bg-slate-700 hover:bg-slate-800' : ''} data-testid="employees-archive-toggle">
          <Archive className="h-4 w-4 mr-1" />{showArchived ? 'Arşivden Çık' : 'Arşiv'}
        </Button>
        <div className="flex items-center gap-2">
          <Label>Statü:</Label>
          <Select value={selectedStatusFilter} onValueChange={setSelectedStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Durum seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="eksik">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                  Eksik
                </span>
              </SelectItem>
              <SelectItem value="beklemede">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  Beklemede
                </span>
              </SelectItem>
              <SelectItem value="onay">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Onaylı
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2 max-w-[250px]">
          <Label>Personel Ara:</Label>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Ad, soyad veya T.C."
              value={employeeSearchQuery}
              onChange={(e) => { markScreenStart('personel_arama'); setEmployeeSearchQuery(e.target.value); }}
              className="pl-8"
              data-testid="employee-search"
            />
          </div>
        </div>
        
        {/* MYK Filtresi */}
        {uniqueMykList.length > 0 && (
          <div className="flex items-center gap-2">
            <Label className="flex items-center gap-1">
              <GraduationCap className="h-4 w-4 text-blue-600" />
              MYK:
            </Label>
            <Select value={selectedMykFilter} onValueChange={(v) => { setSelectedMykFilter(v); if (v !== 'all') setMykStatusFilter('onay'); }}>
              <SelectTrigger className="w-64" data-testid="myk-filter">
                <SelectValue placeholder="MYK seçin" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">Tüm MYK'lar</SelectItem>
                {uniqueMykList.map((myk) => (
                  <SelectItem key={myk.name} value={myk.name}>
                    <span className="flex items-center justify-between gap-2 w-full">
                      <span className="truncate max-w-[180px]">{myk.name}</span>
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {myk.count} kişi
                      </Badge>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Label className="flex items-center gap-1"><GraduationCap className="h-4 w-4 text-blue-600" />MYK Durumu:</Label>
          <Select value={mykStatusFilter} onValueChange={setMykStatusFilter}>
            <SelectTrigger className="w-48" data-testid="myk-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[['all', 'Tümü'], ['onay', 'Onaylı'], ['suresi_gecmis', 'Süresi Geçen'], ['beklemede', 'Onay Bekleyen'], ['eksik', 'Eksik / Hatalı']].map(([v, l]) => (
                <SelectItem key={v} value={v} data-testid={`myk-status-option-${v}`}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Results info */}
      <div className="text-sm text-slate-500">
        {filteredEmployees.length} / {employees.length} personel gösteriliyor
        {selectedActiveFilter === 'passive' && <span className="ml-2 text-slate-500">• Pasif personeller</span>}
        {employeeSearchQuery.length >= 3 && <span className="ml-2 text-red-600">• &ldquo;{employeeSearchQuery}&rdquo; araması aktif</span>}
        {selectedMykFilter !== 'all' && (
          <span className="ml-2 text-blue-600 flex items-center gap-1 inline-flex">
            • <GraduationCap className="h-3.5 w-3.5" /> &ldquo;{selectedMykFilter}&rdquo; MYK filtresi aktif
          </span>
        )}
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10">
                  {filteredEmployees.some(isEmployeeDeficient) && (
                    <Checkbox
                      checked={filteredEmployees.filter(isEmployeeDeficient).every(e => selectedForReport.includes(e.id))}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  )}
                </TableHead>
                <TableHead className="font-semibold text-center">Statü</TableHead>
                <TableHead className="font-semibold text-center">Aktif</TableHead>
                <TableHead className="font-semibold text-center">Ad Soyad</TableHead>
                <TableHead className="font-semibold text-center">T.C. / Pasaport No</TableHead>
                <TableHead className="font-semibold text-center">Görevi</TableHead>
                <TableHead className="font-semibold text-center">İşletme</TableHead>
                <TableHead className="font-semibold text-center">Firma</TableHead>
                <TableHead className="font-semibold text-center">Eklenme</TableHead>
                <TableHead className="font-semibold text-center">Bildirge</TableHead>
                <TableHead className="font-semibold text-center">Eğitim</TableHead>
                <TableHead className="font-semibold text-center">Sağlık</TableHead>
                <TableHead className="font-semibold text-center">MYK</TableHead>
                <TableHead className="font-semibold text-center">ME</TableHead>
                <TableHead className="font-semibold text-center">KKD</TableHead>
                <TableHead className="font-semibold text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={16} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Users className="h-8 w-8 text-slate-300" />
                      {employees.length === 0 ? (
                        <>
                          <p className="text-slate-500">Henüz personel eklenmemiş</p>
                          {canCreate('employees') && <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleOpenDialog()}
                          >
                            İlk personeli ekle
                          </Button>}
                        </>
                      ) : (
                        <>
                          <p className="text-slate-500">Bu filtreye uygun personel bulunamadı</p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedStatusFilter('all');
                              setSelectedActiveFilter('all');
                              setCurrentPage(1);
                            }}
                          >
                            Tüm personelleri göster
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee) => (
                  <TableRow key={employee.id} className={`table-row-hover ${employee.is_active === false ? 'opacity-60 bg-slate-50' : ''}`}>
                    <TableCell>
                      {isEmployeeDeficient(employee) && (
                        <Checkbox
                          checked={selectedForReport.includes(employee.id)}
                          onCheckedChange={() => toggleReportSelection(employee.id)}
                          data-testid={`select-employee-${employee.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-center">{getOverallStatusBadge(employee)}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={employee.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                        {employee.is_active !== false ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {highlightMatch(`${employee.name} ${employee.surname}`, employeeSearchQuery)}
                      {employee.block_info && (
                        <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700" title={isReviewer && employee.block_info.reason ? `Açıklama: ${employee.block_info.reason}` : 'Bloklanmıştır'} data-testid={`employee-blocked-badge-${employee.id}`}>
                          <Ban className="h-3 w-3" />{employee.is_blocked ? 'Bloklu' : 'Blok planlandı'} · {formatDate(employee.block_info.start_date)} – {employee.block_info.end_date ? formatDate(employee.block_info.end_date) : 'Süresiz'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-slate-600 text-sm">
                      {employee.tc_kimlik_no ? (
                        <span className="font-mono" data-testid={`employee-tc-${employee.id}`}>{maskTcNo(employee.tc_kimlik_no)}<PassportTag employee={employee} /></span>
                      ) : (
                        <span className="text-slate-400 text-xs">T.C. Girilmemiş</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-slate-600">
                      {employee.position || '-'}
                    </TableCell>
                    <TableCell className="text-center text-slate-600 text-sm">
                      {getLocationName(employee.company_id)}
                    </TableCell>
                    <TableCell className="text-center">
                      {getCompanyName(employee.company_id)}
                      <p className={`text-[11px] ${employee.on_behalf_of ? 'text-sky-700' : 'text-slate-400'}`} data-testid={`employee-behalf-${employee.id}`}>
                        {employee.on_behalf_of ? `${employee.on_behalf_of} adına` : 'Kendi firması'}
                      </p>
                    </TableCell>
                    <TableCell className="text-center text-slate-500 text-sm">
                      {employee.created_at ? formatDate(employee.created_at.split('T')[0]) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getDocStatusBadge(employee, 'sgk_ise_giris', EMPLOYEE_DOCS[0])}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getDocStatusBadge(employee, 'isg_egitimi', EMPLOYEE_DOCS[1])}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getDocStatusBadge(employee, 'saglik_raporu', EMPLOYEE_DOCS[2])}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getDocStatusBadge(employee, 'mesleki_yeterlilik', EMPLOYEE_DOCS[3])}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getDocStatusBadge(employee, 'mesleki_egitim', EMPLOYEE_DOCS[4])}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getDocStatusBadge(employee, 'kkd_zimmet', EMPLOYEE_DOCS[5])}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {isReviewer && !showArchived && (employee.block_info ? (
                          <Button variant="outline" size="sm" onClick={() => handleUnblock(employee)} className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`unblock-employee-${employee.id}`}>Bloğu Kaldır</Button>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => setBlockTarget(employee)} className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`block-employee-${employee.id}`}><Ban className="h-4 w-4 mr-1" />Blokla</Button>
                        ))}
                        {canEdit('employees') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenDialog(employee)}
                            data-testid={`edit-employee-${employee.id}`}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Güncelle
                          </Button>
                        )}
                        {canDelete('employees') && (showArchived ? (
                          <Button variant="outline" size="sm" onClick={() => handleRestore(employee.id)} className="h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid={`restore-employee-${employee.id}`}>
                            <ArchiveRestore className="h-4 w-4 mr-1" />Geri Getir
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(employee.id)}
                            title="Arşive al"
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            data-testid={`delete-employee-${employee.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingEmployee ? 'Personel Düzenle' : 'Yeni Personel'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="company">Firma</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                  required
                >
                  <SelectTrigger data-testid="employee-company-select">
                    <SelectValue placeholder="Firma seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {authorizedCompanies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="full_name">Ad Soyad</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Personel adı soyadı"
                  required
                  data-testid="employee-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Görevi</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder="Personelin görevi"
                  data-testid="employee-position-input"
                />
              </div>
              <div className="space-y-2">
                <NationalityToggle value={formData.nationality || 'tc'} testPrefix="employee-nationality"
                  onChange={(v) => { setFormData({ ...formData, nationality: v, tc_kimlik_no: '' }); setTcError(null); }} />
                <Label htmlFor="tc_kimlik_no" className="block">
                  {formData.nationality === 'yabanci' ? 'Pasaport No' : 'T.C. Kimlik No'}
                  {(formData.nationality === 'yabanci' || (systemSettings.tc_required && !editingEmployee)) && (
                    <span className="text-rose-500 ml-1">*</span>
                  )}
                </Label>
                <Input
                  id="tc_kimlik_no"
                  value={formData.tc_kimlik_no}
                  onChange={(e) => {
                    const value = formData.nationality === 'yabanci' ? cleanPassport(e.target.value) : e.target.value.replace(/\D/g, '').slice(0, 11);
                    setFormData({ ...formData, tc_kimlik_no: value });
                    setTcError(null);
                  }}
                  placeholder={formData.nationality === 'yabanci' ? 'Pasaport numarası' : '11 haneli T.C. Kimlik No'}
                  maxLength={formData.nationality === 'yabanci' ? 30 : 11}
                  data-testid="employee-tc-input"
                  className={tcError ? 'border-rose-500' : ''}
                />
                {tcError && (
                  <p className="text-xs text-rose-500">{tcError}</p>
                )}
                {!tcError && formData.nationality !== 'yabanci' && formData.tc_kimlik_no && formData.tc_kimlik_no.length === 11 && (
                  <p className="text-xs text-emerald-600">T.C. Kimlik No gecerli</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Durum</Label>
                <Select
                  value={formData.is_active ? 'active' : 'passive'}
                  onValueChange={(value) => setFormData({ ...formData, is_active: value === 'active' })}
                >
                  <SelectTrigger data-testid="employee-status-select">
                    <SelectValue placeholder="Durum seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        Aktif
                      </span>
                    </SelectItem>
                    <SelectItem value="passive">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                        Pasif
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-employee-btn">
                {editingEmployee ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deficiency Selection Dialog */}
      <Dialog open={showDeficiencyList} onOpenChange={(open) => {
        setShowDeficiencyList(open);
        if (!open) setSelectedDeficiencies([]);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Eksiklik Seçin - {EMPLOYEE_DOCS.find(d => d.key === selectedDocKey)?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              {getTemplatesForDoc(selectedDocKey).length === 0 ? (
                <p className="text-slate-500 text-center py-4">
                  Bu belge türü için henüz eksiklik şablonu eklenmemiş.
                  <br />
                  <span className="text-sm">Eksiklik Şablonları sayfasından ekleyebilirsiniz.</span>
                </p>
              ) : (
                getTemplatesForDoc(selectedDocKey).map((template) => (
                  <label
                    key={template.id}
                    className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                      selectedDeficiencies.includes(template.template_text)
                        ? 'border-rose-300 bg-rose-50'
                        : 'border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                    }`}
                  >
                    <Checkbox
                      checked={selectedDeficiencies.includes(template.template_text)}
                      onCheckedChange={() => toggleDeficiency(template.template_text)}
                    />
                    <span className="flex-1">{template.template_text}</span>
                  </label>
                ))
              )}
            </div>
            {selectedDeficiencies.length > 0 && (
              <div className="mt-4 p-3 bg-rose-50 rounded-lg border border-rose-200">
                <p className="text-sm text-rose-700 font-medium">Seçilen eksiklikler ({selectedDeficiencies.length}):</p>
                <p className="text-sm text-rose-600 mt-1">{selectedDeficiencies.join(', ')}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeficiencyList(false);
              setSelectedDeficiencies([]);
            }}>
              İptal
            </Button>
            <Button 
              onClick={handleDeficiencySave}
              disabled={selectedDeficiencies.length === 0}
              className="bg-rose-600 hover:bg-rose-700"
            >
              <X className="h-4 w-4 mr-2" />
              Eksik Kaydet ({selectedDeficiencies.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Warning Dialog */}
      <AlertDialog open={!!dateWarning} onOpenChange={() => setDateWarning(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Tarih Uyarısı
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dateWarning}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setDateWarning(null)}>
              Anladım
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Eksik Raporu E-posta ile Gönder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              {selectedForReport.length} personel için eksik belge raporu PDF olarak e-posta ile gönderilecek.
            </p>
            
            {/* Quick Recipients */}
            {emailRecipients.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Son Kullanılan Adresler</Label>
                <div className="flex flex-wrap gap-2">
                  {emailRecipients.slice(0, 6).map((r, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => addQuickEmail(r.email, 'to')}
                      className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-md text-slate-600"
                    >
                      {r.email}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="to_emails">Alıcı (To) *</Label>
              <Input
                id="to_emails"
                value={toEmails}
                onChange={(e) => setToEmails(e.target.value)}
                placeholder="ornek@email.com, diger@email.com"
                data-testid="to-emails-input"
              />
              <p className="text-xs text-slate-400">Birden fazla adres için virgül kullanın</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cc_emails">Bilgi (CC)</Label>
              <Input
                id="cc_emails"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="bilgi@email.com"
                data-testid="cc-emails-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEmailModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={sendEmailReport}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={sendingEmail}
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Gönder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MYK Selector Dialog */}
      <Dialog open={showMykSelector} onOpenChange={(open) => !open && closeMykSelector()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Mesleki Yeterlilik Belgesi
              {mykSelectorStatus === 'onay' && <Badge className="bg-emerald-100 text-emerald-700">Onay</Badge>}
              {mykSelectorStatus === 'eksik' && <Badge className="bg-rose-100 text-rose-700">Eksik</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Seçim Türü Butonları */}
            <div className="flex gap-2">
              <Button
                variant={!showDiplomaInput ? "default" : "outline"}
                size="sm"
                onClick={() => setShowDiplomaInput(false)}
                className={!showDiplomaInput ? "bg-slate-900" : ""}
              >
                MYK Listesinden Seç
              </Button>
              <Button
                variant={showDiplomaInput ? "default" : "outline"}
                size="sm"
                onClick={() => setShowDiplomaInput(true)}
                className={showDiplomaInput ? "bg-slate-900" : ""}
              >
                Diploma (Elle Giriş)
              </Button>
            </div>

            {!showDiplomaInput ? (
              <>
                {/* MYK Listesi Arama ve Seçim */}
                <div className="space-y-3">
                  <div className="relative">
                    <Input
                      placeholder="MYK belgesi ara..."
                      value={mykSearchQuery}
                      onChange={(e) => setMykSearchQuery(e.target.value)}
                      className="pr-8"
                      data-testid="myk-search-input"
                    />
                    {mykSearchQuery && (
                      <button
                        onClick={() => setMykSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {filteredMykList.length > 0 ? (
                      filteredMykList.map((myk, idx) => {
                        // Benzersiz tanımlayıcı: id varsa id, yoksa code+description+idx
                        const mykUniqueId = myk.id || `${myk.code || ''}-${myk.description || ''}-${idx}`;
                        const isSelected = selectedMykObj && (
                          (myk.id && selectedMykObj.id === myk.id) ||
                          (!myk.id && selectedMykObj.code === myk.code && selectedMykObj.description === myk.description)
                        );
                        
                        return (
                          <button
                            key={mykUniqueId}
                            onClick={() => setSelectedMykObj(isSelected ? null : myk)}
                            className={`w-full text-left px-4 py-2.5 border-b last:border-b-0 hover:bg-slate-50 transition-colors ${
                              isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                            }`}
                          >
                            <div className="font-medium text-sm">{myk.name}</div>
                            {(myk.code || myk.description) && (
                              <div className="text-xs text-slate-500 mt-0.5">
                                {myk.code && <span className="mr-3">Kod: {myk.code}</span>}
                                {myk.description && <span>{myk.description}</span>}
                              </div>
                            )}
                          </button>
                        );
                      })
                    ) : mykSearchQuery ? (
                      <p className="text-sm text-slate-400 text-center py-6">
                        &ldquo;{mykSearchQuery}&rdquo; için sonuç bulunamadı
                      </p>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-6">
                        MYK listesi yüklenmemiş. Eksiklik Şablonları sayfasından Excel yükleyin.
                      </p>
                    )}
                  </div>
                  
                  {mykList.length > 0 && (
                    <p className="text-xs text-slate-400">
                      {filteredMykList.length} / {mykList.length} belge gösteriliyor
                    </p>
                  )}
                </div>

                {/* Geçerlilik Tarihi - sadece onay için ve listeden seçimde */}
                {mykSelectorStatus === 'onay' && selectedMykObj && (
                  <div className="space-y-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <Label className="text-sm font-medium text-emerald-800">
                      Geçerlilik Tarihi *
                    </Label>
                    <Input
                      type="date"
                      value={mykValidityDate}
                      onChange={(e) => setMykValidityDate(e.target.value)}
                      className="bg-white"
                      data-testid="myk-validity-date"
                    />
                    <p className="text-xs text-emerald-600">
                      Belgenin geçerlilik bitiş tarihini girin
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Diploma Elle Giriş */}
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Diploma Adı</Label>
                    <Input
                      placeholder="Örn: Elektrik Mühendisliği Lisans"
                      value={diplomaText}
                      onChange={(e) => setDiplomaText(e.target.value)}
                      data-testid="diploma-text-input"
                    />
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700 flex items-center gap-2">
                      <Check className="h-4 w-4" />
                      Diplomalar süresizdir, geçerlilik tarihi gerekmez.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={closeMykSelector}>
              İptal
            </Button>
            {!showDiplomaInput ? (
              <Button 
                onClick={() => handleMykListSelection(selectedMykObj, mykValidityDate)}
                disabled={!selectedMykObj || (mykSelectorStatus === 'onay' && !mykValidityDate)}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="myk-confirm-btn"
              >
                {mykSelectorStatus === 'onay' ? 'Onayla' : 'Eksik Olarak İşaretle'}
              </Button>
            ) : (
              <Button 
                onClick={handleDiplomaSelection}
                disabled={!diplomaText.trim()}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="diploma-confirm-btn"
              >
                {mykSelectorStatus === 'onay' ? 'Onayla' : 'Eksik Olarak İşaretle'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentViewerDialog
        open={!!viewerDoc}
        onOpenChange={(open) => !open && setViewerDoc(null)}
        subtitle={viewerDoc ? `${viewerDoc.employee.name} ${viewerDoc.employee.surname}` : ''}
        entity={viewerDoc?.employee}
        docKey={viewerDoc?.docKey}
        docLabel={viewerDoc?.label}
        mode="approved"
      />
      {blockTarget && <BlockEmployeeDialog employee={blockTarget} onClose={() => setBlockTarget(null)} onDone={() => { setBlockTarget(null); fetchEmployees(); }} />}
    </div>
    </TooltipProvider>
  );
};

export default EmployeesPage;
